import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import StructureImage from '../../components/chemistry/StructureImage';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Input' },
  { name: 'Configure' },
  { name: 'Calculate' },
  { name: 'Results' },
];

const WorkbenchQuantum: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [inputText, setInputText] = useState(ctxSmiles || '');
  const [selectedExample, setSelectedExample] = useState('');
  const [inputFormat, setInputFormat] = useState('smi');
  const [charge, setCharge] = useState(0);
  const [spin, setSpin] = useState(0);
  const [qmethod, setQmethod] = useState('HF');
  const [basis, setBasis] = useState('sto-3g');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const runCalculation = async () => {
    if (!inputText.trim()) {
      setError('No molecule provided. Please go back to Step 1 and enter a molecule.');
      return;
    }
    setIsRunning(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/homo-lumo/calculate', {
        smiles: inputText,
      }, { signal: controller.signal });
      const data = resp.data;
      setResult(data);
      setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
      setStep(3);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') setError('Calculation cancelled by user');
      else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : err.message || 'Calculation failed');
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={(s) => {
        if (s <= step || completedSteps.includes(s - 1)) setStep(s);
      }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="quantum" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: PySCF</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} label="Stop Calculation" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 0 && (
              <div>
                <h2 style={h2Style}>Enter Molecule</h2>
                <p style={descStyle}>Molecule input for HOMO/LUMO orbital energy calculation.</p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                  {[{ name: 'Water', smi: 'O' }, { name: 'Ethanol', smi: 'CCO' }, { name: 'Benzene', smi: 'c1ccccc1' }].map((ex) => (
                    <button key={ex.smi} onClick={() => { setInputText(ex.smi); setSelectedExample(ex.name); }}
                      style={{ ...btnStyle, ...(inputText === ex.smi ? { background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#f59e0b' } : {}) }}>
                      {ex.name} <StructureImage smiles={ex.smi} size={24} />
                    </button>
                  ))}
                </div>
                <input type="text" value={inputText} onChange={(e) => { setInputText(e.target.value); setSelectedExample(''); }}
                  placeholder="Enter molecule notation..." style={{ ...textareaStyle, height: 40 }} />
                {selectedExample && inputText && (
                  <div style={{ marginTop: 4, color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>Selected: {selectedExample} <StructureImage smiles={inputText} size={24} /></div>
                )}
                <div style={{ marginTop: 8 }}>
                  <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    Format:{' '}
                    <select value={inputFormat} onChange={(e) => setInputFormat(e.target.value)} style={selectStyle}>
                      <option value="smi">SMILES</option>
                      <option value="xyz">XYZ</option>
                    </select>
                  </label>
                </div>
                <button onClick={() => { if (inputText.trim()) { setCompletedSteps((prev) => Array.from(new Set([...prev, 0]))); setStep(1); } }}
                  disabled={!inputText.trim()} style={{ ...primaryBtnStyle, marginTop: 12, opacity: inputText.trim() ? 1 : 0.5 }}>Continue</button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Calculation Settings</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400 }}>
                  <div>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13, display: 'block', marginBottom: 6 }}>Method:</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {['HF', 'B3LYP'].map((m) => (
                        <button key={m} onClick={() => setQmethod(m)}
                          style={{ ...btnStyle, ...(qmethod === m ? { background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#f59e0b' } : {}) }}>
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13, display: 'block', marginBottom: 6 }}>Basis set:</label>
                    <select value={basis} onChange={(e) => setBasis(e.target.value)} style={selectStyle}>
                      <option value="sto-3g">STO-3G (minimal, fast)</option>
                      <option value="3-21g">3-21G</option>
                      <option value="6-31g">6-31G</option>
                      <option value="6-31g*">6-31G* (polarized)</option>
                      <option value="cc-pvdz">cc-pVDZ</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      Charge:{' '}
                      <input type="number" value={charge} onChange={(e) => setCharge(parseInt(e.target.value) || 0)}
                        style={{ ...selectStyle, width: 60 }} />
                    </label>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      Spin:{' '}
                      <input type="number" value={spin} onChange={(e) => setSpin(parseInt(e.target.value) || 0)}
                        min={0} style={{ ...selectStyle, width: 60 }} />
                    </label>
                  </div>
                </div>
                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 1]))); setStep(2); }}
                  style={{ ...primaryBtnStyle, marginTop: 20 }}>Continue</button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Run Quantum Calculation</h2>
                {/* HEAVY WARNING */}
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ color: '#fb7185', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                    Quantum calculations can take 5-30 minutes for larger molecules
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {qmethod}/{basis} | Charge: {charge} | Spin: {spin}
                  </div>
                </div>
                <p style={descStyle}>
                  Molecule: <StructureImage smiles={inputText} size={48} />
                </p>
                {!isRunning && !result && (
                  <button onClick={runCalculation} disabled={!inputText.trim()} style={{ ...primaryBtnStyle, fontSize: 15, padding: '12px 24px', opacity: inputText.trim() ? 1 : 0.5 }}>
                    Run Calculation
                  </button>
                )}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} />
                    <span style={{ color: '#f59e0b', fontSize: 14 }}>Computing HOMO/LUMO energies... this may take several minutes</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 3 && result && (
              <div>
                <h2 style={h2Style}>HOMO/LUMO Results</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {[
                    { key: 'homo_hartree', label: 'HOMO (E\u2095)' },
                    { key: 'lumo_hartree', label: 'LUMO (E\u2095)' },
                    { key: 'gap_ev', label: 'Gap (eV)' },
                    { key: 'nelec', label: 'Electrons' },
                    { key: 'nocc', label: 'Occupied MOs' },
                  ].map(({ key, label }) => (
                    result[key] != null && (
                      <div key={key} style={resultCardStyle}>
                        <div style={resultLabelStyle}>{label}</div>
                        <div style={resultValueStyle}>
                          {typeof result[key] === 'number' ? result[key].toFixed(4) : result[key]}
                        </div>
                      </div>
                    )
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))} style={btnStyle}>Copy Results</button>
                  <button onClick={() => {
                    const lines = ['parameter,value', ...Object.entries(result).map(([k, v]) => `${k},${v}`)];
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'homo_lumo_results.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="Quantum" toolKey="quantum" smiles={ctxSmiles || inputText} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0 }}>
            <MethodCard engine="PySCF" title="HOMO/LUMO Orbital Energies"
              description="Hartree-Fock or DFT calculation of molecular orbital energies. HOMO (highest occupied) and LUMO (lowest unoccupied) define the fundamental gap and chemical reactivity."
              equation="Gap = E(LUMO) - E(HOMO)"
              parameters={[
                { name: 'HF', desc: 'Hartree-Fock (mean-field, no correlation)' },
                { name: 'B3LYP', desc: 'DFT hybrid functional (includes correlation)' },
                { name: 'STO-3G', desc: 'Minimal basis (fast, qualitative)' },
                { name: '6-31G*', desc: 'Split-valence + polarization (quantitative)' },
              ]}
              reference="Sun et al. (2018) WIREs Comput Mol Sci 8:e1340"
              referenceUrl="https://doi.org/10.1002/wcms.1340"
              source="PySCF (Python Simulations of Chemistry Framework)" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' };
const selectStyle: React.CSSProperties = { padding: '4px 8px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13 };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 14px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' };

export default WorkbenchQuantum;
